/**
 * Home Approach Data Graph® — Proprietary event tracking system.
 * Silently captures behavioral, journey, content, and financial signals
 * to build a rich member journey dataset.
 */

import { supabase } from "@/integrations/supabase/client";

export type DataGraphCategory = "behavioral" | "journey" | "content" | "financial" | "compliance";

export interface DataGraphEvent {
  event_category: DataGraphCategory;
  event_type: string;
  event_data?: Record<string, any>;
  page_path?: string;
}

// Simple session ID (persists per tab)
const sessionId =
  typeof window !== "undefined"
    ? (sessionStorage.getItem("dg_session") ??
      (() => {
        const id = crypto.randomUUID();
        sessionStorage.setItem("dg_session", id);
        return id;
      })())
    : "ssr";

/**
 * Attribution context attached to every event. Reads localStorage flags set by
 * lead-source funnels (LifeMart, etc.) so we can filter cohorts in the admin
 * dashboard without having to instrument each call site.
 */
function getAttributionContext(): Record<string, any> {
  if (typeof window === "undefined") return {};
  const ctx: Record<string, any> = {};
  try {
    if (localStorage.getItem("lifemart_access_verified") === "true") {
      ctx.lifemart = true;
      ctx.lead_source = ctx.lead_source ?? "lifemart";
    }
  } catch {}
  return ctx;
}

// In-memory queue for batching
let queue: DataGraphEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH = 25;

async function flush() {
  if (queue.length === 0) return;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    // Only track authenticated users
    queue = [];
    return;
  }

  const batch = queue.splice(0, MAX_BATCH);

  const rows = batch.map((e) => ({
    user_id: user.id,
    event_category: e.event_category,
    event_type: e.event_type,
    event_data: e.event_data ?? {},
    page_path: e.page_path ?? (typeof window !== "undefined" ? window.location.pathname : null),
    session_id: sessionId,
  }));

  const { error } = await supabase.from("data_graph_events" as any).insert(rows);

  if (error && import.meta.env.DEV) {
    console.warn("[Data Graph®] flush error:", error.message);
  }

  // If there's more in the queue, flush again
  if (queue.length > 0) {
    flush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

const BOT_UA_PATTERN =
  /bot|crawl|spider|slurp|headless|phantomjs|puppeteer|playwright|scrapy|curl|wget|python-requests|semrush|ahrefs|dataforseo|bytespider|petalbot|gptbot|claudebot|ccbot/i;

/**
 * Automated traffic (crawlers, scrapers, headless browsers) inflates event
 * counts and skews engagement reporting. Drop those hits before they queue.
 */
function isAutomatedClient(): boolean {
  if (typeof navigator === "undefined") return false;
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver) return true;
  return BOT_UA_PATTERN.test(navigator.userAgent || "");
}

/**
 * Track a Data Graph® event. Events are batched and flushed every 5s.
 */
export function dgTrack(
  category: DataGraphCategory,
  eventType: string,
  data?: Record<string, any>
) {
  if (isAutomatedClient()) return;

  const attribution = getAttributionContext();

  queue.push({
    event_category: category,
    event_type: eventType,
    event_data: { ...attribution, ...data },
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
  });

  if (queue.length >= MAX_BATCH) {
    flush();
  } else {
    scheduleFlush();
  }

  if (import.meta.env.DEV) {
    console.log(`📈 [Data Graph®] ${category}/${eventType}`, data || "");
  }
}

// Flush on page unload
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });
}

// ── Convenience helpers ──

/** Track page view */
export const dgPageView = (pagePath?: string) =>
  dgTrack("behavioral", "page_view", { path: pagePath ?? window.location.pathname });

/** Track tool/calculator usage */
export const dgToolUsed = (toolName: string, inputs?: Record<string, any>) =>
  dgTrack("financial", "calculator_use", { tool: toolName, ...inputs });

/** Track content engagement */
export const dgContentView = (contentType: string, title: string, slug?: string) =>
  dgTrack("content", "content_view", { content_type: contentType, title, slug });

/** Track lesson/course completion */
export const dgLessonComplete = (pathSlug: string, stepTitle: string, points: number) =>
  dgTrack("content", "lesson_complete", { path: pathSlug, step: stepTitle, points });

/** Track journey transition */
export const dgTrackChange = (fromTrack: string, toTrack: string) =>
  dgTrack("journey", "track_change", { from_track: fromTrack, to_track: toTrack });

/** Track milestone completion */
export const dgMilestoneComplete = (track: string, milestoneTitle: string, points: number) =>
  dgTrack("journey", "milestone_complete", { track, milestone: milestoneTitle, points });

/** Track feature click */
export const dgFeatureClick = (feature: string, detail?: string) =>
  dgTrack("behavioral", "feature_click", { feature, detail });

/** Track search */
export const dgSearch = (query: string, resultCount?: number) =>
  dgTrack("behavioral", "search", { query, result_count: resultCount });
